import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { Sun, Moon } from 'lucide-react';

const StickyNavigation = ({ darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const { isLoggedIn, user, signOut, profile } = useAuth();
  const [activeSection, setActiveSection] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const navigationItems = [
    { label: 'Home', sectionId: 'hero', offset: 0, isHome: true },
    { label: 'Pricing', sectionId: 'pricing', offset: 80 },
    { label: 'How It Works', sectionId: 'how-it-works', offset: 80 },
    { label: 'Reviews', sectionId: 'reviews', offset: 80 },
    { label: 'FAQ', sectionId: 'faq', offset: 80 },
    { label: 'Get Help', sectionId: 'get-help', offset: 80 }
  ];

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);

      // Show navigation after hero section (approximately 100vh)
      setIsVisible(scrollTop > window.innerHeight * 0.8);

      // Update active section based on scroll position
      const sections = navigationItems.map(item => document.getElementById(item.sectionId));
      const currentSection = sections.find(section => {
        if (section) {
          const rect = section.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });

      if (currentSection) {
        setActiveSection(currentSection.id);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Detect when modals are open
  useEffect(() => {
    const checkForModals = () => {
      const modalSelectors = [
        '[role="dialog"]',
        '.modal-overlay',
        '.fixed.inset-0.bg-black',
        '.fixed.inset-0.bg-gray-600',
        '.ReactModal__Overlay',
        'div[class*="fixed"][class*="inset-0"][class*="bg-black"]',
        'div[class*="fixed"][class*="inset-0"][class*="bg-gray"]'
      ];
      
      const hasOpenModal = modalSelectors.some(selector => {
        const elements = document.querySelectorAll(selector);
        return Array.from(elements).some(el => {
          const style = window.getComputedStyle(el);
          return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
        });
      });
      
      setIsModalOpen(hasOpenModal);
    };

    checkForModals();
    
    const observer = new MutationObserver(checkForModals);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style']
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (sectionId, offset = 80) => {
    // Special handling for home/hero section
    if (sectionId === 'hero') {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } else {
      const element = document.getElementById(sectionId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleDashboardClick = () => {
    console.log('Navbar - Dashboard button clicked');
    console.log('Navbar - User:', user);
    console.log('Navbar - Profile:', profile);
    
    if (profile?.is_admin || profile?.is_super_admin) {
      console.log('Navbar - Navigating to admin dashboard');
      navigate('/admin/dashboard');
    } else if (profile?.is_writer) {
      console.log('Navbar - Navigating to writer dashboard');
      navigate('/writer/dashboard');
    } else if (profile?.is_qa) {
      console.log('Navbar - Navigating to qa dashboard');
      navigate('/qa/dashboard');
    } else if (profile?.is_client) {
      console.log('Navbar - Navigating to client dashboard');
      navigate('/client/dashboard');
    } else {
      console.log('Navbar - No specific role, staying on landing page');
    }
  };

  if (!isVisible || isModalOpen) return null;

  return (
    <>
      {/* Progress Indicator */}
      <div className="fixed top-0 left-0 w-full h-0.5 bg-surface z-110">
        <div 
          className="h-full bg-gradient-to-r from-primary to-secondary transition-all duration-250 ease-smooth"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>

      {/* Sticky Navigation */}
      <nav className="sticky-navigation fixed top-0 left-0 right-0 bg-white/5 backdrop-blur-md border-b border-white/10 z-100 animate-slide-down">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex-shrink-0">
              <div className="flex items-center space-x-2">
                <div className="relative w-8 h-8 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center shadow-lg">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-graduation-cap">
                    <path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"></path>
                    <path d="M22 10v6"></path><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"></path>
                  </svg>
                </div>
                <span className="text-xl font-bold text-gradient font-accent">
                  SOMO Academia
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center justify-between flex-1 ml-10">
              {/* Navigation Links */}
              <div className="flex items-center space-x-8">
                {navigationItems.map((item) => (
                  <button
                    key={item.sectionId}
                    onClick={() => scrollToSection(item.sectionId, item.offset)}
                    className={`nav-link px-3 py-2 rounded-md text-sm font-medium transition-smooth ${
                      activeSection === item.sectionId ? 'active' : ''
                    }`}
                    aria-label={`Navigate to ${item.label} section`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
                
              {/* Right side buttons */}
              <div className="flex items-center space-x-3">
                {/* Theme Toggle */}
                <button
                  onClick={toggleDarkMode}
                  className="theme-toggle glass-card p-2.5 rounded-xl btn-ios-feedback"
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? <Sun size={16} /> : <Moon size={16} />}
                </button>
                
                {/* Dashboard Button (only if logged in) */}
                {isLoggedIn && (
                  <button
                    onClick={handleDashboardClick}
                    className="glass-card text-white px-4 py-2.5 rounded-xl text-sm font-medium btn-ios-feedback flex items-center space-x-2"
                    aria-label="Go to dashboard"
                  >
                    <Icon name="LayoutDashboard" size={16} />
                    <span>Dashboard</span>
                  </button>
                )}
                
                {/* Login/Logout Button */}
                {isLoggedIn ? (
                  <button
                    onClick={signOut}
                    className="glass-card text-white px-4 py-2.5 rounded-xl text-sm font-medium btn-ios-feedback flex items-center space-x-2"
                    aria-label="Logout"
                  >
                    <Icon name="LogOut" size={16} />
                    <span>Logout</span>
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/login')}
                    className="bg-primary hover:bg-primary-600 text-white px-4 py-2.5 rounded-xl text-sm font-medium btn-ios-feedback magnetic-btn flex items-center space-x-2"
                    aria-label="Login to your account"
                  >
                    <Icon name="LogIn" size={16} />
                    <span>Login</span>
                  </button>
                )}
              </div>
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-text-secondary hover:text-primary hover:bg-surface transition-smooth"
                aria-expanded="false"
                aria-label="Toggle navigation menu"
              >
                <Icon 
                  name={isMobileMenuOpen ? "X" : "Menu"} 
                  size={24} 
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden animate-slide-down">
            <div className="px-2 pt-2 pb-3 space-y-1 bg-white/10 backdrop-blur-md border-t border-white/20">
              {navigationItems.map((item) => (
                <button
                  key={item.sectionId}
                  onClick={() => scrollToSection(item.sectionId, item.offset)}
                  className={`nav-link block px-3 py-2 rounded-md text-base font-medium w-full text-left transition-smooth ${
                    activeSection === item.sectionId ? 'active bg-primary/10' : 'hover:bg-surface-800'
                  }`}
                  aria-label={`Navigate to ${item.label} section`}
                >
                  {item.label}
                </button>
              ))}
              
              {/* Mobile Theme Toggle */}
              <button
                onClick={toggleDarkMode}
                className="theme-toggle bg-white/10 backdrop-blur-md text-white px-3 py-2 rounded-md text-base font-medium w-full text-left transition-all duration-300 border border-white/20 shadow-lg hover:shadow-xl flex items-center mt-2"
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun size={18} className="mr-2" /> : <Moon size={18} className="mr-2" />}
                {darkMode ? 'Light Mode' : 'Dark Mode'}
              </button>
              
              {/* Mobile Dashboard Button (only if logged in) */}
              {isLoggedIn && (
                <button
                  onClick={() => {
                    handleDashboardClick();
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-white/10 backdrop-blur-md text-white px-3 py-2 rounded-md text-base font-medium w-full text-left transition-all duration-300 border border-white/20 shadow-lg hover:shadow-xl flex items-center mt-2"
                  aria-label="Go to dashboard"
                >
                  <Icon name="LayoutDashboard" size={18} className="mr-2" />
                  Dashboard
                </button>
              )}
              
              {/* Mobile Login/Logout */}
              {isLoggedIn ? (
                <button
                  onClick={() => {
                    signOut();
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-white/10 backdrop-blur-md text-white px-3 py-2 rounded-md text-base font-medium w-full text-left transition-all duration-300 border border-white/20 shadow-lg hover:shadow-xl flex items-center mt-2"
                  aria-label="Logout"
                >
                  <Icon name="LogOut" size={18} className="mr-2" />
                  Logout
                </button>
              ) : (
                <button
                  onClick={() => {
                    navigate('/login');
                    setIsMobileMenuOpen(false);
                  }}
                  className="bg-primary hover:bg-primary-600 text-white px-3 py-2 rounded-md text-base font-medium w-full text-left transition-smooth flex items-center mt-2"
                  aria-label="Login to your account"
                >
                  <Icon name="LogIn" size={18} className="mr-2" />
                  Login
                </button>
              )}
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default StickyNavigation;
